/**
 * convertBranchToForm
 *
 * Converts a raw branch object (from getAllV3 cache or single-branch GET) into
 * the shape the AddBranch form expects — the C-final shape per map-A-to-C-form.md.
 *
 * Extracted from AddBranch.loadData:853-917. Behaviour is identical to the
 * inline transforms that ran there; extraction makes it testable in isolation.
 *
 * Rules:
 * - Pure function: does NOT mutate the `branch` argument. All transforms work on
 *   new arrays / spread copies.
 * - No React dependency, no side effects (no toasts — caller handles hadSanitized).
 * - Hub deserializers are delegated to existing hubOptionTransform exports; this
 *   module does not duplicate their logic.
 * - audioPath: branch.audioPath takes precedence (flag OFF path — server provides it);
 *   falls back to deriveAudioBucketUrl(audios) (flag ON path — cache lacks it).
 */

import { StepTypeMode } from '../../../../dorian-shared/types/branch/Branch';
import type { Branch } from '../../../../dorian-shared/types/branch/Branch';
import type { Audio as ECAudio } from '../../../../dorian-shared/types/audio/audio';
import { sanitizeCheckStepValue } from './Steps/checkStepValidation';
import type { MemoryDTO } from '../../../pages/Book/MemoryBank/memoryBankTypes';
import {
  deserializeHubOptionFromApi,
  deserializeHubSettingsFromApi,
} from './Steps/StepTypeHub/hubOptionTransform';
import { deriveAudioBucketUrl } from '../../../ui/Preview/deriveAudioBucketUrl';

// ---------------------------------------------------------------------------
// Return type
// ---------------------------------------------------------------------------

/**
 * A form step after conversion. The shape is dynamic — which of check / switch /
 * hubSettings / hubOptions exist depends on the step type, and the converter adds
 * runtime-only fields (intValue, varValue, deserialized uuids) the strict
 * BranchStep type does not declare. So a step is an index-readable record, not a
 * strict BranchStep — this matches what transformStep actually produces.
 */
export type FormStep = Record<string, any>;

export type ConvertBranchToFormResult = {
  // Always an array — convertBranchToForm maps over rawSteps (never undefined).
  steps: FormStep[];
  stepTypeMode: StepTypeMode;
  locationId: number | '';
  analytics: object | null;
  audioPath: string | null;
  /** True when at least one check step had an incompatible value that was cleared.
   *  Caller (AddBranch.loadData) is responsible for showing the warning toast. */
  hadSanitized: boolean;
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Applies conditionList coercions to a single switch item.
 * Returns a new object — does not mutate the input.
 * Mirrors AddBranch.loadData:863-884.
 */
function coerceSwitchItem(item: Record<string, unknown>): Record<string, unknown> {
  const out = { ...item };

  if (out.gotoBranchId !== undefined) {
    out.gotoBranchId = Number(out.gotoBranchId);
  }

  // 'number' → 'intValue' (string) for frontend text input
  if (out.number !== undefined) {
    out.intValue = String(out.number);
    delete out.number;
  }

  // 'variable' → 'varValue' (number)
  if (out.variable !== undefined) {
    out.varValue = Number(out.variable);
    delete out.variable;
  }

  return out;
}

/**
 * Applies all AddBranch.loadData step-level transforms to a single step.
 * Returns a new step object — the input is not mutated.
 * Order: (A) conditionList coercion → (B) sanitize → (C) hub deserialize.
 */
function transformStep(
  rawStep: Record<string, unknown>,
  memoryBank: MemoryDTO[],
): { step: Record<string, unknown>; sanitized: boolean } {
  // Work on a shallow copy of the step; check/switch fields get new objects below.
  const step: Record<string, unknown> = { ...rawStep };

  // --- Block A: conditionList coercion ----------------------------------------
  const rawCheck = step.check as Record<string, unknown> | null | undefined;
  if (rawCheck && rawCheck.operator === 'conditionList') {
    const check = { ...rawCheck };

    if (check.variableId !== undefined) {
      check.variableId = Number(check.variableId);
    }

    if (Array.isArray(step.switch)) {
      step.switch = (step.switch as Record<string, unknown>[]).map(coerceSwitchItem);
    }

    step.check = check;
  }

  // --- Block B: sanitize invalid check values ---------------------------------
  let sanitized = false;
  const check = step.check as Record<string, unknown> | null | undefined;
  if (check && memoryBank.length > 0) {
    // sanitizeCheckStepValue mutates the check object in place.
    // Because step.check is already a new object (spread above for conditionList,
    // or the same object reference for non-conditionList steps), we need a copy
    // here too to avoid mutating the original branch argument.
    const checkCopy = { ...check } as Parameters<typeof sanitizeCheckStepValue>[0];
    if (sanitizeCheckStepValue(checkCopy, memoryBank)) {
      sanitized = true;
      step.check = checkCopy;
    }
  }

  // --- Block C: hub deserialize ------------------------------------------------
  // hubSettings and hubOptions are mutated in place by the deserializers.
  // We deep-copy only what the deserializers touch to avoid mutating the original.
  const rawHubSettings = step.hubSettings as object | null | undefined;
  if (rawHubSettings) {
    // Shallow-clone the settings object; deserializer replaces progressBars array.
    const hubSettingsCopy = { ...rawHubSettings } as Parameters<typeof deserializeHubSettingsFromApi>[0];
    deserializeHubSettingsFromApi(hubSettingsCopy);
    step.hubSettings = hubSettingsCopy;
  }

  const rawHubOptions = step.hubOptions as object[] | null | undefined;
  if (Array.isArray(rawHubOptions)) {
    // Shallow-clone each option object; deserializer sets fields on it.
    step.hubOptions = rawHubOptions.map((opt) => {
      const optCopy = { ...opt } as Parameters<typeof deserializeHubOptionFromApi>[0];
      deserializeHubOptionFromApi(optCopy);
      return optCopy;
    });
  }

  // --- Block D: clone stepAudios array ----------------------------------------
  // The shallow step copy aliases the cached branch's stepAudios array, and the
  // audio settings modal mutates that array in place (pushes fallback SFX/Music
  // rows). Clone the array so editing the form never writes into the shared cache.
  if (Array.isArray(step.stepAudios)) {
    step.stepAudios = [...(step.stepAudios as unknown[])];
  }

  return { step, sanitized };
}

// ---------------------------------------------------------------------------
// Main export
// ---------------------------------------------------------------------------

export function convertBranchToForm(
  branch: Branch | Record<string, unknown>,
  memoryBank: MemoryDTO[],
  audios: ECAudio[] = [],
): ConvertBranchToFormResult {
  const rawSteps = Array.isArray(branch.steps) ? branch.steps : [];

  let hadSanitized = false;
  const steps = rawSteps.map((rawStep) => {
    const { step, sanitized } = transformStep(rawStep as Record<string, unknown>, memoryBank);
    if (sanitized) hadSanitized = true;
    return step;
  });

  const stepTypeMode = (branch.stepTypeMode as StepTypeMode | undefined) ?? StepTypeMode.Regular;

  const rawLocationId = branch.locationId;
  const locationId: number | '' = rawLocationId !== null && rawLocationId !== undefined
    ? Number(rawLocationId)
    : '';

  const analytics = branch.analytics ? (branch.analytics as object) : null;

  // Prefer the server-provided audioPath (flag OFF, HTTP response carries it).
  // Fall back to derivation from the audios list (flag ON, cache never carries audioPath).
  const audioPath = (branch.audioPath as string | null | undefined)
    ?? deriveAudioBucketUrl(audios);

  return {
    steps: steps as FormStep[],
    stepTypeMode,
    locationId,
    analytics,
    audioPath,
    hadSanitized,
  };
}
